import React, { useCallback, useEffect, useState } from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
    Checkbox,
    FormControlLabel,
    TextField,
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { showNotification } from '../App';
import { client } from '..';
import { MatterTerminology } from '../MatterTerminology';
import { RoleCategorySelector } from '../components/RoleCategorySelector';
import { RoleSelector } from '../components/RoleSelector';
import { mainTheme } from '../Theme';
import { EditorWrapper } from '../components/Editor';
import { UserAutocompleteSelector } from '../components/UserAutocompleteSelector';
import {
    // DeleteEmailTemplate,
    EmailTemplateData,
    EmailTemplateQueryParams,
    EmailTemplateTypeEnum,
    retrieveEmailTemplateData,
    subscriptionEmailTemplate,
    UpdateEmailTemplate,
} from './EmailTemplateRespository';
import { ContentLayout, ContentLayoutProps } from '../v2/layouts/ContentLayout';
import { ScopeSelector, ScopeSelectorProps, ScopeValue } from './selectors/ScopeSelector';
import { IAutoCompleteItem } from '../typings/autoComplete';
import { SkeletonLoader } from '../components/loading/SkeletonLoader';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
        },
        // ── editor-card: the single card wrapping cc row / subject row / editor ──
        editorCard: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            backgroundColor: '#ffffff',
            // border: '1px solid rgba(0, 0, 0, 0.12)',
            // borderRadius: 8,
            // overflow: 'hidden',
            // boxShadow: '0 1px 6px rgba(0,0,0,0.14)',
        },
        // ── editor-cc-row ──
        ccRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: '10px 0px',
            borderBottom: '1px solid #f3f4f6',
            fontSize: 13,
            color: '#6b7280',
            flexShrink: 0,
        },
        scopeRow: {
            padding: '12px 0px',
            borderBottom: '1px solid #f3f4f6',
            flexShrink: 0,
        },
        scopeField: {
            minWidth: 0,
        },
        scopeSecondaryField: {
            minWidth: 0,
        },
        ccFormControlLabel: {
            margin: 0,
        },
        ccCheckbox: {
            padding: 4,
            marginRight: theme.spacing(0.5),
        },
        checkedIcon: {
            color: mainTheme.TemplateColor.Primary,
        },
        ccLabelText: {
            fontSize: 13,
            color: '#6b7280',
        },
        // ── subject-row ──
        subjectRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: '15px 0px',
            // borderBottom: '1px solid #f3f4f6',
            flexShrink: 0,
        },
        subjectField: {
            flex: 1,
            // '& .MuiOutlinedInput-input': {
            //     padding: '8px 12px',
            //     fontSize: 14,
            // },
        },
        // ── editor area (TinyMCE lives inside the same card, no extra border) ──
        editorSection: {
            minHeight: 500,
            // padding: '0px 0px 15px 0px',
            display: 'flex',
            flexFlow: 'column',
            flex: 1,
            backgroundColor: '#fff',
            position: 'relative',
            '& > .tox': {
                flex: 1,
            }
        },
        skeletonEditorWrap: {
            flex: 1,
            minHeight: 220,
        },
    })
);

interface EmailTemplateContentProps extends Pick<ContentLayoutProps, 'contentControl'>, Pick<ScopeSelectorProps, 'excludeScope'> {
    emailType?: EmailTemplateTypeEnum;
    showFields?: {
        ccToAssistant?: boolean;
        subject?: boolean;
        scope?: boolean;
    }
    defaultScope?: ScopeValue;
}

interface EmailTemplateContentState {
    isLoading: boolean;
    scope: ScopeValue;
    isInternalRole: string;
    roleId: string;
    user: IAutoCompleteItem;
    isEditorHidden: boolean;
    subject: string;
    isCcToAssistant: boolean;
    editorContent: string;
    onSubjectError: boolean;
}

type EmailTemplateScopeState = Pick<EmailTemplateContentState, 'scope' | 'isInternalRole' | 'roleId' | 'user'>;

export const EmailTemplateContent: React.FC<EmailTemplateContentProps> = ({
    emailType = EmailTemplateTypeEnum.EnquiryDataCollectionCompletion,
    contentControl,
    showFields = {
        ccToAssistant: true,
        subject: true,
        scope: false,
    },
    excludeScope = [],
    defaultScope: defaultScopeProp = 'DashboardSecurityScope',
}) => {
    const classes = useStyles();
    const shouldFetchImmediately = !showFields.scope || defaultScopeProp === 'DashboardSecurityScope';

    const [state, setState] = useState<EmailTemplateContentState>({
        isLoading: shouldFetchImmediately,
        scope: defaultScopeProp,
        isInternalRole: '',
        roleId: '',
        user: {
            value: '',
            label: '',
        },
        isEditorHidden: !!showFields.scope && !shouldFetchImmediately,
        subject: '',
        isCcToAssistant: false,
        editorContent: '',
        onSubjectError: false,
    });

    const isScopeSelectionComplete = useCallback((currentState: EmailTemplateScopeState): boolean => {
        if (!showFields.scope) {
            return true;
        }

        switch (currentState.scope) {
            case 'DashboardSecurityRoleCategory':
                return currentState.isInternalRole !== '';
            case 'DashboardSecurityRole':
                return currentState.roleId !== '';
            case 'DashboardSecurityUser':
                return currentState.user.value !== '';
            default:
                return true;
        }
    }, [showFields.scope]);

    const buildEmailTemplateQueryParams = useCallback((currentState: EmailTemplateScopeState): EmailTemplateQueryParams => {
        let isInternalRole: boolean | null = null;
        let roleId: string | null = null;
        let selectedUserId: string | null = null;

        switch (currentState.scope) {
            case 'DashboardSecurityRoleCategory':
                isInternalRole = currentState.isInternalRole === '1';
                break;
            case 'DashboardSecurityRole':
                roleId = currentState.roleId || null;
                break;
            case 'DashboardSecurityUser':
                selectedUserId = currentState.user.value || null;
                break;
            default:
                break;
        }

        return {
            isInternalRole,
            roleId,
            selectedUserId,
            emailType: EmailTemplateTypeEnum[emailType],
        };
    }, [emailType]);

    const onDataRetrieved = useCallback((data: EmailTemplateData) => {
        if (data.templates !== null && data.templates.emailTemplate !== null) {
            setState((prevState) => ({
                ...prevState,
                isLoading: false,
                subject: data.templates.emailTemplate.subject,
                isCcToAssistant: data.templates.emailTemplate.ccAssistant,
                editorContent: data.templates.emailTemplate.template,
            }));
            return;
        }

        setState((prevState) => ({
            ...prevState,
            isLoading: false,
        }));
    }, []);

    const fetchEmailTemplateData = useCallback((currentState: EmailTemplateScopeState) => {
        retrieveEmailTemplateData(
            buildEmailTemplateQueryParams(currentState),
            true,
            // tslint:disable-next-line: no-any
            (data: any) => onDataRetrieved(data),
            // tslint:disable-next-line: no-any
            (reason: any): void => {
                showNotification(null, reason, 'error');
                setState((prevState) => ({
                    ...prevState,
                    isLoading: false,
                }));
            }
        );
    }, [buildEmailTemplateQueryParams, onDataRetrieved]);

    useEffect(() => {
        if (!isScopeSelectionComplete(state)) {
            setState((prevState) => (
                prevState.isLoading
                    ? {
                        ...prevState,
                        isLoading: false,
                    }
                    : prevState
            ));
            return;
        }

        fetchEmailTemplateData(state);
    }, [emailType]);

    useEffect(() => {
        const subscribeEmailObj = client
            .subscribe({
                fetchPolicy: 'no-cache',
                query: subscriptionEmailTemplate,
                variables: {},
            })
            .subscribe({
                // tslint:disable-next-line:no-any
                next(results: any) {
                    if (results.data.emailTemplateNotification.status) {
                        const { emailTemplateNotification } = results.data;
                        showNotification('Success', emailTemplateNotification.message, 'info');
                    } else {
                        showNotification('Failed', results.data.emailTemplateNotification.message, 'error');
                    }
                },
                // tslint:disable-next-line:no-any
                error(err: any) {
                    // tslint:disable-next-line:no-console
                    console.error(err);
                    showNotification(null, err, 'error');
                },
            });

        return () => {
            subscribeEmailObj.unsubscribe();
        };
    }, []);

    const updateEmailTemplate = useCallback(() => {
        if (state.subject === '') {
            setState((prevState) => ({
                ...prevState,
                onSubjectError: true,
            }));
            return;
        }

        setState((prevState) => ({
            ...prevState,
            isLoading: true,
            onSubjectError: false,
        }));

        client
            .mutate({
                mutation: UpdateEmailTemplate,
                variables: {
                    ...buildEmailTemplateQueryParams(state),
                    template: state.editorContent,
                    subject: state.subject && state.subject.length > 0 ? state.subject : null,
                    ccAssistant: state.isCcToAssistant,
                },
            })
            .then((results) => {
                if (results.data) {
                    if (results.data.error === null || results.data.error === undefined) {
                        showNotification(null, 'Successfully submitted', 'info');
                        setState((prevState) => ({
                            ...prevState,
                            isLoading: false,
                        }));
                    } else {
                        showNotification('Failed to save email template', results.data.error, 'error');
                        setState((prevState) => ({
                            ...prevState,
                            isLoading: false,
                        }));
                    }
                }
            })
            // tslint:disable-next-line:no-any
            .catch((reason: any) => {
                showNotification('Failed to Save email template', reason, 'error');
                setState((prevState) => ({
                    ...prevState,
                    isLoading: false,
                }));
            });
    }, [buildEmailTemplateQueryParams, state]);

    // const deleteEmailTemplate = useCallback(() => {
    //     setState((prevState) => ({
    //         ...prevState,
    //         isLoading: true,
    //     }));

    //     client
    //         .mutate({
    //             mutation: DeleteEmailTemplate,
    //             variables: {
    //                 isInternalRole: null,
    //                 roleId: null,
    //                 selectedUserId: null,
    //                 emailType: EmailTemplateTypeEnum[emailType],
    //             },
    //         })
    //         .then((results) => {
    //             if (results.data) {
    //                 if (results.data.error === null || results.data.error === undefined) {
    //                     showNotification(null, 'Successfully deleted', 'info');
    //                     setState((prevState) => ({
    //                         ...prevState,
    //                         isLoading: false,
    //                         subject: '',
    //                         isCcToAssistant: false,
    //                         editorContent: '',
    //                     }));
    //                 } else {
    //                     showNotification('Failed to delete email template', results.data.error, 'error');
    //                     setState((prevState) => ({
    //                         ...prevState,
    //                         isLoading: false,
    //                     }));
    //                 }
    //             }
    //         })
    //         // tslint:disable-next-line:no-any
    //         .catch((reason: any) => {
    //             showNotification('Failed to delete email template', reason, 'error');
    //             setState((prevState) => ({
    //                 ...prevState,
    //                 isLoading: false,
    //             }));
    //         });
    // }, [emailType]);

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        setState((prevState) => ({
            ...prevState,
            subject: nextValue,
            onSubjectError: nextValue.trim() === '',
        }));
    };

    const onCcToAssistantChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setState((prevState) => ({
            ...prevState,
            isCcToAssistant: checked,
        }));
    };

    const onSelection = (selection: IAutoCompleteItem, name: string) => {
        handleFilterChange(selection, name);
    };

    const onUserSelection = (selection: IAutoCompleteItem | IAutoCompleteItem[], name: string) => {
        if (Array.isArray(selection)) {
            return;
        }

        handleFilterChange(selection, name);
    };

    const handleFilterChange = (selection: IAutoCompleteItem, name: string) => {
        const nextState = {
            ...state,
            isLoading: true,
            onSubjectError: false,
        };

        if (name === 'scopeId') {
            nextState.scope = selection.value as ScopeValue;
            nextState.isInternalRole = '';
            nextState.roleId = '';
            nextState.user = {
                value: '',
                label: '',
            };
            nextState.isEditorHidden = selection.value !== 'DashboardSecurityScope';
        }

        if (name === 'roleCategoryId') {
            nextState.isInternalRole = selection.value;
            nextState.roleId = '';
            nextState.user = {
                value: '',
                label: '',
            };
            nextState.isEditorHidden = selection.value === '';
        }

        if (name === 'roleId') {
            nextState.isInternalRole = '';
            nextState.roleId = selection.value;
            nextState.user = {
                value: '',
                label: '',
            };
            nextState.isEditorHidden = selection.value === '';
        }

        if (name === 'userId') {
            nextState.isInternalRole = '';
            nextState.roleId = '';
            nextState.user = {
                value: selection.value,
                label: selection.label,
            };
            nextState.isEditorHidden = selection.value === '';
        }

        setState(nextState);

        if (!isScopeSelectionComplete(nextState)) {
            return;
        }

        fetchEmailTemplateData(nextState);
    };

    const secondSelection = (scope: string) => {
        switch (scope) {
            case 'DashboardSecurityRoleCategory':
                return (
                    <RoleCategorySelector
                        label={MatterTerminology.RoleCategory}
                        name="roleCategoryId"
                        value={state.isInternalRole}
                        onSelection={onSelection}
                        variant="outlined"
                        size="small"
                    />
                );
            case 'DashboardSecurityRole':
                return (
                    <RoleSelector
                        label={MatterTerminology.Role}
                        name="roleId"
                        value={state.roleId}
                        onSelection={onSelection}
                        variant="outlined"
                        size="small"
                    />
                );
            case 'DashboardSecurityUser':
                return (
                    <UserAutocompleteSelector
                        label={MatterTerminology.User}
                        name="userId"
                        onSelection={onUserSelection}
                        value={state.user}
                        isAuthorized={true}
                        variant="outlined"
                        size="small"
                    />
                );
            default:
                return <div />;
        }
    };

    const onEditorChange = (content: string) => {
        setState((prevState) => ({
            ...prevState,
            editorContent: content,
        }));
    };

    return (
        <ContentLayout
            contentControl={{
                ...contentControl,
                // title: state.subject,
                actions: {
                    onSave: updateEmailTemplate,
                    // onDelete: deleteEmailTemplate,
                },
                loading: state.isLoading,
            }}
        >
            <div className={classes.root}>
                <div className={classes.editorCard}>
                    {showFields.scope && (
                        <div className={classes.scopeRow}>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={12} md={6} lg={6} className={classes.scopeField}>
                                    <ScopeSelector
                                        label={MatterTerminology.Scope}
                                        name="scopeId"
                                        value={state.scope}
                                        className="scope-selector"
                                        variant="outlined"
                                        size="small"
                                        onSelection={onSelection}
                                        defaultValue={{ value: 'DashboardSecurityScope', label: 'Firm' }}
                                        excludeScope={excludeScope}
                                    />
                                </Grid>
                                <Grid item={true} xs={12} md={6} lg={6} className={classes.scopeSecondaryField}>
                                    {secondSelection(state.scope)}
                                </Grid>
                            </Grid>
                        </div>
                    )}

                    {/* ── CC to Assistant row ── */}
                    {showFields.ccToAssistant && (
                        <div className={classes.ccRow}>
                            {state.isLoading ? (
                                <SkeletonLoader variant="text" rows={1} widths={[250]} heights={[20]} />
                            ) : (
                                <FormControlLabel
                                    className={classes.ccFormControlLabel}
                                    control={(
                                        <Checkbox
                                            className={classes.ccCheckbox}
                                            checked={state.isCcToAssistant}
                                            checkedIcon={<CheckBoxIcon className={classes.checkedIcon} />}
                                            onChange={onCcToAssistantChange}
                                            name="isCcToAssistant"
                                            disabled={state.isLoading}
                                            size="small"
                                        />
                                    )}
                                    label={<span className={classes.ccLabelText}>CC to Assistant</span>}
                                />
                            )}
                        </div>
                    )}

                    {/* ── Subject row ── */}
                    {showFields.subject && !state.isEditorHidden && (
                        <div className={classes.subjectRow}>
                            {state.isLoading ? (
                                <SkeletonLoader variant="text" rows={1} widths={[250]} heights={[32]} />
                            ) : (
                                <TextField
                                    className={classes.subjectField}
                                    variant="outlined"
                                    label="Subject"
                                    value={state.subject}
                                    onChange={handleTextChange}
                                    disabled={state.isLoading}
                                    error={state.onSubjectError}
                                    helperText={state.onSubjectError ? 'Subject is required' : ''}
                                    size="small"
                                    required={true}
                                />
                            )}
                        </div>
                    )}

                    {/* ── Editor ── */}
                    {!state.isEditorHidden && (
                        <div className={classes.editorSection}>
                            {state.isLoading ? (
                                <div className={classes.skeletonEditorWrap}>
                                    <SkeletonLoader
                                        variant="text"
                                        rows={10}
                                        widths={['100%']}
                                    />
                                </div>
                            ) : (
                                <EditorWrapper
                                    value={state.editorContent}
                                    onEditorChange={onEditorChange}
                                    disabled={state.isLoading}
                                    emailType={emailType}
                                />
                            )}
                        </div>
                    )}

                </div>
            </div>
        </ContentLayout>
    );
};